import React, { createContext, useContext, useState, useEffect } from 'react';
import { normalizeExtraLabel } from '../lib/extras';
import { getDisplayedBasePriceReais, getStandardProductPriceReais } from '../lib/pricing';
import { getExtraPriceCents, getSelectedExtrasUnitTotalReais } from '../lib/extraPricing';

const CartContext = createContext({});

function normalizeSelectedExtras(extras) {
  if (!Array.isArray(extras)) return [];

  return extras
    .filter((extra) => extra && typeof extra === 'object')
    .map((extra) => {
      const quantity = Math.max(1, Number(extra.quantity || 1));
      const priceCents = getExtraPriceCents(extra);
      return {
        id: String(extra.id || extra.name || ''),
        name: normalizeExtraLabel(extra.name),
        quantity,
        price_cents: priceCents,
        subtotal_cents: priceCents * quantity,
      };
    })
    .filter((extra) => extra.name)
    .sort((a, b) => {
      const aKey = `${a.id}|${a.name}`.toLowerCase();
      const bKey = `${b.id}|${b.name}`.toLowerCase();
      if (aKey < bKey) return -1;
      if (aKey > bKey) return 1;
      return 0;
    });
}

function normalizeRemovalNote(value) {
  const note = String(value || '').trim();
  if (!note) return '';
  return note.replace(/\s+/g, ' ').toLowerCase();
}

function buildCartItemKey(productId, selectedExtras, removalNote = '') {
  const extrasKey = normalizeSelectedExtras(selectedExtras)
    .map((extra) => `${extra.id || extra.name}:${extra.quantity}`)
    .join('|');
  const removeKey = normalizeRemovalNote(removalNote);
  if (!extrasKey && !removeKey) return String(productId);

  const parts = [String(productId)];
  if (extrasKey) parts.push(`extras=${extrasKey}`);
  if (removeKey) parts.push(`remove=${removeKey}`);
  return parts.join('::');
}

function matchesCartItem(item, targetKeyOrId) {
  if (!targetKeyOrId) return false;
  return item?.cart_item_key === targetKeyOrId || item?.id === targetKeyOrId;
}

function resolveItemPrice(item) {
  const value = getDisplayedBasePriceReais(item);

  const selectedExtras = normalizeSelectedExtras(item?.selected_extras);
  const extrasUnitValue = getSelectedExtrasUnitTotalReais(selectedExtras);

  return value + extrasUnitValue;
}

export function CartProvider({ children }) {
  const [items, setItems] = useState([]);
  const [restaurantId, setRestaurantId] = useState(null);
  const [restaurantName, setRestaurantName] = useState('');

  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      try {
        const parsed = JSON.parse(savedCart);
        const restoredItems = Array.isArray(parsed.items) ? parsed.items.map((item, index) => {
          const selectedExtras = normalizeSelectedExtras(item?.selected_extras);
          const removalNote = String(item?.removal_note || item?.removed_ingredients || '').trim();
          return {
            ...item,
            selected_extras: selectedExtras,
            removal_note: removalNote,
            removed_ingredients: removalNote,
            cart_item_key: item?.cart_item_key || buildCartItemKey(item?.id || `item-${index}`, selectedExtras, removalNote),
          };
        }) : [];
        setItems(restoredItems);
        setRestaurantId(parsed.restaurantId);
        setRestaurantName(parsed.restaurantName || '');
      } catch (err) {
        console.error('Erro ao carregar carrinho:', err);
      }
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify({
      items,
      restaurantId,
      restaurantName
    }));
  }, [items, restaurantId, restaurantName]);

  function addItem(product, restaurant) {
    if (restaurantId && restaurantId !== restaurant.id) {
      const confirmChange = window.confirm(
        `Seu carrinho possui itens de ${restaurantName}. Deseja limpar o carrinho e adicionar itens de ${restaurant.name}?`
      );
      if (!confirmChange) return false;

      setItems([]);
      setRestaurantId(restaurant.id);
      setRestaurantName(restaurant.name);
    } else if (!restaurantId) {
      setRestaurantId(restaurant.id);
      setRestaurantName(restaurant.name);
    }

    setItems(prev => {
      const selectedExtras = normalizeSelectedExtras(product?.selected_extras);
      const removalNote = String(product?.removal_note || product?.removed_ingredients || '').trim();
      const cartItemKey = product?.cart_item_key || buildCartItemKey(product?.id, selectedExtras, removalNote);
      const existingIndex = prev.findIndex(item => item.cart_item_key === cartItemKey);

      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex].quantity = (updated[existingIndex].quantity || 0) + (product.quantity || 1);
        return updated;
      }

      return [...prev, {
        ...product,
        quantity: product.quantity || 1,
        selected_extras: selectedExtras,
        removal_note: removalNote,
        removed_ingredients: removalNote,
        cart_item_key: cartItemKey,
      }];
    });

    return true;
  }

  function syncProductCustomization(product, selectedExtras, removalNote = '', sourceCartItemKey = null) {
    if (!product || typeof product !== 'object' || !product.id) return;

    const normalizedExtras = normalizeSelectedExtras(selectedExtras);
    const normalizedRemoval = String(removalNote || '').trim();
    const targetKey = buildCartItemKey(product.id, normalizedExtras, normalizedRemoval);

    setItems((prev) => {
      let currentIndex = -1;
      if (sourceCartItemKey) {
        currentIndex = prev.findIndex((item) => (item?.cart_item_key || String(item?.id)) === String(sourceCartItemKey));
      }

      if (currentIndex < 0) {
        const sameProductIndexes = prev
          .map((item, index) => ({ item, index }))
          .filter(({ item }) => String(item?.id) === String(product.id));

        if (sameProductIndexes.length !== 1) return prev;
        currentIndex = sameProductIndexes[0].index;
      }

      const currentItem = prev[currentIndex];
      const currentKey = currentItem?.cart_item_key || String(currentItem?.id);
      if (currentKey === targetKey) return prev;

      const existingTargetIndex = prev.findIndex((item) => item?.cart_item_key === targetKey);
      const productSnapshot = { ...currentItem, ...product };
      const updatedItem = {
        ...productSnapshot,
        preco: getDisplayedBasePriceReais(productSnapshot),
        preco_original: getStandardProductPriceReais(productSnapshot),
        quantity: currentItem.quantity || 1,
        selected_extras: normalizedExtras,
        removal_note: normalizedRemoval,
        removed_ingredients: normalizedRemoval,
        cart_item_key: targetKey,
      };

      if (existingTargetIndex >= 0 && existingTargetIndex !== currentIndex) {
        const merged = [...prev];
        merged[existingTargetIndex] = {
          ...merged[existingTargetIndex],
          quantity: (merged[existingTargetIndex].quantity || 0) + (currentItem.quantity || 0),
        };
        merged.splice(currentIndex, 1);
        return merged;
      }

      const next = [...prev];
      next[currentIndex] = updatedItem;
      return next;
    });
  }

  function removeItem(productId) {
    setItems(prev => {
      const next = prev.filter(item => !matchesCartItem(item, productId));
      if (next.length === 0) {
        setRestaurantId(null);
        setRestaurantName('');
      }
      return next;
    });
  }

  function updateQuantity(productId, quantity) {
    if (quantity <= 0) {
      removeItem(productId);
      return;
    }

    setItems(prev => prev.map(item => (
      matchesCartItem(item, productId) ? { ...item, quantity } : item
    )));
  }

  function increaseQuantity(productId) {
    setItems(prev => prev.map(item => (
      matchesCartItem(item, productId)
        ? { ...item, quantity: (item.quantity || 0) + 1 }
        : item
    )));
  }

  function decreaseQuantity(productId) {
    setItems(prev => {
      const next = prev
        .map(item => (
          matchesCartItem(item, productId)
            ? { ...item, quantity: Math.max(0, (item.quantity || 0) - 1) }
            : item
        ))
        .filter(item => (item.quantity || 0) > 0);

      if (next.length === 0) {
        setRestaurantId(null);
        setRestaurantName('');
      }

      return next;
    });
  }

  function clearCart() {
    setItems([]);
    setRestaurantId(null);
    setRestaurantName('');
    localStorage.removeItem('cart');
  }

  const subtotal = Array.isArray(items) ? items.reduce((sum, item) => {
    const price = resolveItemPrice(item);
    const quantity = item.quantity || 0;
    return sum + (price * quantity);
  }, 0) : 0;

  const itemCount = Array.isArray(items) ? items.reduce((sum, item) => sum + (item.quantity || 0), 0) : 0;

  return (
    <CartContext.Provider
      value={{
        items,
        restaurantId,
        restaurantName,
        subtotal,
        itemCount,
        addItem,
        removeItem,
        updateQuantity,
        increaseQuantity,
        decreaseQuantity,
        syncProductCustomization,
        clearCart
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  return useContext(CartContext);
}
